export const VEHICLE_TYPES = [
  'Fuel Tanker',
  'Chemical Tanker',
  'Bitumen Tanker',
  'LPG Trailer',
  'Bulk Powder Tanker',
  'Flatbed Trailer',
  'ISO Tank Trailer',
  'Container Trailer',
  'Flexi Tank Container',
  'Vacuum Tanker',
  'Water Tanker',
];

export const INITIAL_TRANSPORT_COMPANIES = [
  'Al Maha Transport LLC',
  'Gulf Fleet Services',
  'Emirates Tanker Co.',
  'ADNOC Logistics Partner',
  'Sharjah Heavy Haulage',
];

/** @deprecated use INITIAL_TRANSPORT_COMPANIES — kept for backward compat */
export const TRANSPORT_COMPANIES = INITIAL_TRANSPORT_COMPANIES;

export const INITIAL_PRODUCT_COMPATIBILITY_OPTIONS = [
  'Petrol',
  'Diesel',
  'Jet Fuel',
  'Crude Oil',
  'Liquid Sulphur',
  'Bitumen',
  'LPG',
  'Water',
];

/** @deprecated use INITIAL_PRODUCT_COMPATIBILITY_OPTIONS — kept for backward compat */
export const PRODUCT_COMPATIBILITY_OPTIONS = INITIAL_PRODUCT_COMPATIBILITY_OPTIONS;

export function mergeProductCatalog(base, extraProducts = []) {
  const catalog = [...base];
  extraProducts.forEach((product) => {
    const trimmed = product?.trim();
    if (!trimmed) return;
    const exists = catalog.some((p) => p.toLowerCase() === trimmed.toLowerCase());
    if (!exists) catalog.push(trimmed);
  });
  return catalog;
}

export const MODAL_COMPLIANCE_FIELDS = [
  { key: 'mulkiyaExpiry', label: 'Mulkiya Expiry', attachLabel: 'Mulkiya Copy', docKey: 'mulkiyaCopy' },
  { key: 'insuranceExpiry', label: 'Insurance Expiry', attachLabel: 'Insurance Copy', docKey: 'insuranceCopy' },
  { key: 'roadPermitExpiry', label: 'Road Permit Expiry', attachLabel: 'Road Permit Copy', docKey: 'roadPermitCopy' },
  { key: 'adnocApprovalExpiry', label: 'ADNOC Approval Expiry', attachLabel: 'ADNOC Copy', docKey: 'adnocCopy' },
  { key: 'enocApprovalExpiry', label: 'ENOC Approval Expiry', attachLabel: 'ENOC Copy', docKey: 'enocCopy' },
];

export const REAR_ATTACHMENT_TYPES = [
  { value: 'none', label: 'Tractor Head Only' },
  { value: 'single-b-double', label: 'Single / B-Double Tanker' },
  { value: 'iso-chassis', label: 'ISO Tank Chassis / Flatbed' },
];

export const EMPTY_COUPLED_UNIT = {
  rearAttachmentType: 'none',
  isoContainerSlots: 1,
  tankerPhoto: null,
  tankerMulkiya: null,
  civilDefenseAdnocPermit: null,
  trailerChassisPhoto: null,
  trailerMulkiya: null,
  isoContainer1Photo: null,
  isoContainer1HydroCert: null,
  isoContainer2Photo: null,
  isoContainer2HydroCert: null,
};

export const EMPTY_ATTACHMENTS = {
  mulkiyaCopy: null,
  insuranceCopy: null,
  roadPermitCopy: null,
  adnocCopy: null,
  enocCopy: null,
};

export const EMPTY_MODAL_FORM = {
  vehicleNumber: '',
  chassisNumber: '',
  engineNumber: '',
  transportCompany: '',
  capacity: '',
  vehicleType: '',
  productCompatibility: [],
  mulkiyaExpiry: '',
  insuranceExpiry: '',
  roadPermitExpiry: '',
  adnocApprovalExpiry: '',
  enocApprovalExpiry: '',
  truckPhoto: null,
  attachments: { ...EMPTY_ATTACHMENTS },
  coupledUnit: { ...EMPTY_COUPLED_UNIT },
};

export function generateMockDocLink(vehicleNumber, docType, fileName) {
  const slug = vehicleNumber.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  return `https://docs.ogts-logicore.ae/fleet/${slug}/${docType}/${encodeURIComponent(fileName)}`;
}

export function simulateFileAttachment(file, vehicleNumber, docType) {
  return {
    fileName: file.name,
    mockUrl: generateMockDocLink(vehicleNumber || 'draft', docType, file.name),
    uploadedAt: new Date().toISOString().slice(0, 10),
  };
}

export function simulateTruckPhoto(file, vehicleNumber) {
  return {
    fileName: file.name,
    previewUrl: URL.createObjectURL(file),
    mockUrl: generateMockDocLink(vehicleNumber || 'draft', 'truck-photo', file.name),
    uploadedAt: new Date().toISOString().slice(0, 10),
  };
}

export function simulateCoupledAsset(file, vehicleNumber, assetKey, isImage = false) {
  if (isImage) {
    return {
      fileName: file.name,
      previewUrl: URL.createObjectURL(file),
      mockUrl: generateMockDocLink(vehicleNumber || 'draft', assetKey, file.name),
      uploadedAt: new Date().toISOString().slice(0, 10),
    };
  }
  return simulateFileAttachment(file, vehicleNumber, assetKey);
}

function mockCoupledPhoto(vehicleNumber, assetKey, fileName, previewUrl) {
  return {
    fileName,
    previewUrl,
    mockUrl: generateMockDocLink(vehicleNumber, assetKey, fileName),
    uploadedAt: '2026-01-15',
  };
}

function mockCoupledDoc(vehicleNumber, assetKey, fileName) {
  return {
    fileName,
    mockUrl: generateMockDocLink(vehicleNumber, assetKey, fileName),
    uploadedAt: '2026-01-15',
  };
}

const MOCK_TANKER_PHOTO =
  'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=640&h=400&fit=crop';
const MOCK_TRAILER_PHOTO =
  'https://images.unsplash.com/photo-1519003724024-9a87bab113f3?w=640&h=400&fit=crop';
const MOCK_ISO_PHOTO =
  'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=640&h=400&fit=crop';

const MOCK_TRUCK_PHOTO =
  'https://images.unsplash.com/photo-1601584114707-776987970781?w=640&h=400&fit=crop';

export const MOCK_FLEET_VEHICLES = [
  {
    id: 'VH-001',
    vehicleNumber: 'DXB-T-4021',
    chassisNumber: 'WDB9340231L123456',
    engineNumber: 'ENG-OM471-8842',
    transportCompany: 'Al Maha Transport LLC',
    capacity: '42,000 L',
    vehicleType: 'Fuel Tanker',
    productCompatibility: ['Diesel', 'Petrol'],
    status: 'Active',
    truckPhoto: {
      fileName: 'dxb_t_4021_front.jpg',
      previewUrl: MOCK_TRUCK_PHOTO,
      mockUrl: generateMockDocLink('DXB-T-4021', 'truck-photo', 'dxb_t_4021_front.jpg'),
      uploadedAt: '2026-01-15',
    },
    attachments: {
      mulkiyaCopy: {
        fileName: 'mulkiya_expiry_2026.pdf',
        mockUrl: generateMockDocLink('DXB-T-4021', 'mulkiya', 'mulkiya_expiry_2026.pdf'),
        uploadedAt: '2026-01-10',
      },
      insuranceCopy: {
        fileName: 'insurance_policy_2026.pdf',
        mockUrl: generateMockDocLink('DXB-T-4021', 'insurance', 'insurance_policy_2026.pdf'),
        uploadedAt: '2026-01-10',
      },
      roadPermitCopy: {
        fileName: 'road_permit_2026.pdf',
        mockUrl: generateMockDocLink('DXB-T-4021', 'road-permit', 'road_permit_2026.pdf'),
        uploadedAt: '2026-01-12',
      },
      adnocCopy: {
        fileName: 'adnoc_approval_2026.pdf',
        mockUrl: generateMockDocLink('DXB-T-4021', 'adnoc', 'adnoc_approval_2026.pdf'),
        uploadedAt: '2026-01-08',
      },
      enocCopy: {
        fileName: 'enoc_approval_2026.pdf',
        mockUrl: generateMockDocLink('DXB-T-4021', 'enoc', 'enoc_approval_2026.pdf'),
        uploadedAt: '2026-01-08',
      },
    },
    coupledUnit: {
      rearAttachmentType: 'single-b-double',
      isoContainerSlots: 1,
      tankerPhoto: mockCoupledPhoto('DXB-T-4021', 'tanker-photo', 'tanker_dxb_4021.jpg', MOCK_TANKER_PHOTO),
      tankerMulkiya: mockCoupledDoc('DXB-T-4021', 'tanker-mulkiya', 'tanker_mulkiya_4021.pdf'),
      civilDefenseAdnocPermit: mockCoupledDoc('DXB-T-4021', 'civil-defense-adnoc', 'civil_defense_adnoc_4021.pdf'),
      trailerChassisPhoto: null,
      trailerMulkiya: null,
      isoContainer1Photo: null,
      isoContainer1HydroCert: null,
      isoContainer2Photo: null,
      isoContainer2HydroCert: null,
    },
  },
  {
    id: 'VH-002',
    vehicleNumber: 'SHJ-C-8810',
    chassisNumber: 'VF611GFA000987654',
    engineNumber: 'ENG-D13K-2291',
    transportCompany: 'Gulf Fleet Services',
    capacity: '38 MT',
    vehicleType: 'Chemical Tanker',
    productCompatibility: ['Crude Oil', 'Liquid Sulphur'],
    status: 'Active',
    truckPhoto: {
      fileName: 'shj_c_8810_side.jpg',
      previewUrl: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=640&h=400&fit=crop',
      mockUrl: generateMockDocLink('SHJ-C-8810', 'truck-photo', 'shj_c_8810_side.jpg'),
      uploadedAt: '2026-02-01',
    },
    attachments: {
      mulkiyaCopy: {
        fileName: 'mulkiya_shj_8810.pdf',
        mockUrl: generateMockDocLink('SHJ-C-8810', 'mulkiya', 'mulkiya_shj_8810.pdf'),
        uploadedAt: '2026-02-01',
      },
      insuranceCopy: {
        fileName: 'insurance_shj_8810.pdf',
        mockUrl: generateMockDocLink('SHJ-C-8810', 'insurance', 'insurance_shj_8810.pdf'),
        uploadedAt: '2026-02-01',
      },
      roadPermitCopy: null,
      adnocCopy: {
        fileName: 'adnoc_cert_8810.pdf',
        mockUrl: generateMockDocLink('SHJ-C-8810', 'adnoc', 'adnoc_cert_8810.pdf'),
        uploadedAt: '2026-02-03',
      },
      enocCopy: null,
    },
    coupledUnit: {
      rearAttachmentType: 'iso-chassis',
      isoContainerSlots: 2,
      tankerPhoto: null,
      tankerMulkiya: null,
      civilDefenseAdnocPermit: null,
      trailerChassisPhoto: mockCoupledPhoto('SHJ-C-8810', 'trailer-chassis', 'trailer_shj_8810.jpg', MOCK_TRAILER_PHOTO),
      trailerMulkiya: mockCoupledDoc('SHJ-C-8810', 'trailer-mulkiya', 'trailer_mulkiya_8810.pdf'),
      isoContainer1Photo: mockCoupledPhoto('SHJ-C-8810', 'iso-container-1', 'iso_cont1_8810.jpg', MOCK_ISO_PHOTO),
      isoContainer1HydroCert: mockCoupledDoc('SHJ-C-8810', 'iso-hydro-1', 'hydro_test_cont1_8810.pdf'),
      isoContainer2Photo: mockCoupledPhoto('SHJ-C-8810', 'iso-container-2', 'iso_cont2_8810.jpg', MOCK_ISO_PHOTO),
      isoContainer2HydroCert: mockCoupledDoc('SHJ-C-8810', 'iso-hydro-2', 'inspection_cont2_8810.pdf'),
    },
  },
  {
    id: 'VH-003',
    vehicleNumber: 'AUH-L-1155',
    chassisNumber: 'YV2RT40A0JB654321',
    engineNumber: 'ENG-CURSOR-7710',
    transportCompany: 'Emirates Tanker Co.',
    capacity: '56,000 L',
    vehicleType: 'LPG Trailer',
    productCompatibility: ['LPG', 'Bitumen'],
    status: 'Active',
    truckPhoto: null,
    attachments: {
      mulkiyaCopy: {
        fileName: 'mulkiya_auh_1155.pdf',
        mockUrl: generateMockDocLink('AUH-L-1155', 'mulkiya', 'mulkiya_auh_1155.pdf'),
        uploadedAt: '2026-01-20',
      },
      insuranceCopy: null,
      roadPermitCopy: null,
      adnocCopy: null,
      enocCopy: {
        fileName: 'enoc_clearance_1155.pdf',
        mockUrl: generateMockDocLink('AUH-L-1155', 'enoc', 'enoc_clearance_1155.pdf'),
        uploadedAt: '2026-01-22',
      },
    },
    coupledUnit: { ...EMPTY_COUPLED_UNIT, rearAttachmentType: 'none' },
  },
];

export const MOCK_COMPLIANCE_MATRIX = [
  {
    vehicleNumber: 'DXB-T-4021',
    mulkiyaExpiry: '2026-08-15',
    insuranceExpiry: '2026-12-01',
    roadPermitExpiry: '2026-09-30',
    adnocApprovalExpiry: '2026-06-18',
    enocApprovalExpiry: '2026-11-20',
    portPassExpiry: '2026-07-05',
    hseCompliant: true,
  },
  {
    vehicleNumber: 'SHJ-C-8810',
    mulkiyaExpiry: '2026-10-22',
    insuranceExpiry: '2027-01-15',
    roadPermitExpiry: '2026-10-01',
    adnocApprovalExpiry: '2026-09-14',
    enocApprovalExpiry: '2026-08-28',
    portPassExpiry: '2026-09-01',
    hseCompliant: true,
  },
  {
    vehicleNumber: 'AUH-L-1155',
    mulkiyaExpiry: '2026-07-25',
    insuranceExpiry: '2026-08-01',
    roadPermitExpiry: '2026-07-10',
    adnocApprovalExpiry: '2026-06-14',
    enocApprovalExpiry: '2026-07-20',
    portPassExpiry: '2026-06-20',
    hseCompliant: false,
  },
];

export const COMPLIANCE_COLUMNS = [
  { key: 'mulkiyaExpiry', label: 'Mulkiya Expiry' },
  { key: 'insuranceExpiry', label: 'Insurance Expiry' },
  { key: 'roadPermitExpiry', label: 'Road Permit' },
  { key: 'adnocApprovalExpiry', label: 'ADNOC Approval' },
  { key: 'enocApprovalExpiry', label: 'ENOC Approval' },
  { key: 'portPassExpiry', label: 'Port Pass' },
];

function futureDate(daysFromNow) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10);
}

export function generateVehicleId(vehicles) {
  const nums = vehicles.map((v) => parseInt(v.id.replace('VH-', ''), 10));
  const next = Math.max(0, ...nums) + 1;
  return `VH-${String(next).padStart(3, '0')}`;
}

export function buildComplianceFromForm(form, vehicleNumber, existingRecord = null) {
  return {
    vehicleNumber,
    mulkiyaExpiry: form.mulkiyaExpiry || futureDate(200),
    insuranceExpiry: form.insuranceExpiry || futureDate(365),
    roadPermitExpiry: form.roadPermitExpiry || futureDate(120),
    adnocApprovalExpiry: form.adnocApprovalExpiry || futureDate(90),
    enocApprovalExpiry: form.enocApprovalExpiry || futureDate(45),
    portPassExpiry: existingRecord?.portPassExpiry || futureDate(180),
    hseCompliant: existingRecord?.hseCompliant ?? true,
  };
}

export function vehicleToModalForm(vehicle, complianceRecord) {
  return {
    vehicleNumber: vehicle.vehicleNumber,
    chassisNumber: vehicle.chassisNumber || '',
    engineNumber: vehicle.engineNumber || '',
    transportCompany: vehicle.transportCompany || '',
    capacity: vehicle.capacity || '',
    vehicleType: vehicle.vehicleType || '',
    productCompatibility: vehicle.productCompatibility || [],
    mulkiyaExpiry: complianceRecord?.mulkiyaExpiry || '',
    insuranceExpiry: complianceRecord?.insuranceExpiry || '',
    roadPermitExpiry: complianceRecord?.roadPermitExpiry || '',
    adnocApprovalExpiry: complianceRecord?.adnocApprovalExpiry || '',
    enocApprovalExpiry: complianceRecord?.enocApprovalExpiry || '',
    truckPhoto: vehicle.truckPhoto || null,
    attachments: {
      ...EMPTY_ATTACHMENTS,
      ...(vehicle.attachments || {}),
    },
    coupledUnit: {
      ...EMPTY_COUPLED_UNIT,
      ...(vehicle.coupledUnit || {}),
    },
  };
}

export function formatCapacity(capacity) {
  if (!capacity) return '';
  if (
    capacity.includes('L') ||
    capacity.includes('MT') ||
    capacity.includes('l')
  ) {
    return capacity;
  }
  return `${capacity} L`;
}
